import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Modal dialog that indicates a successful cancellation and exposes actions for the user.
 * @component
 * @selector app-cancellation-success-modal
 * @example
 *   <app-cancellation-success-modal
 *     [isVisible]="true"
 *     (okay)="handleOkay()"
 *     (createAnother)="handleCreateAnother()"
 *     (close)="handleClose()">
 *   </app-cancellation-success-modal>
 * @input {boolean} isVisible - Controls whether the modal is visible. Set true to show the modal.
 * @output {EventEmitter<void>} okay - Emitted when the user confirms or acknowledges the success (OK action).
 * @output {EventEmitter<void>} createAnother - Emitted when the user chooses to create another item after the cancellation.
 * @output {EventEmitter<void>} close - Emitted when the modal should be closed or dismissed.
 *
 * Key methods:
 * - onOkay(): void - Invokes emit() on the 'okay' EventEmitter to notify parent components of the OK action.
 * - onCreateAnother(): void - Invokes emit() on the 'createAnother' EventEmitter to request creating another item.
 * - onClose(): void - Invokes emit() on the 'close' EventEmitter to request closing the modal.
 *
 * Lifecycle hooks:
 * - This component is a simple presentational/emit-only component and does not implement Angular lifecycle hooks like OnInit or OnDestroy.
 */
@Component({
  selector: 'app-cancellation-success-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cancellation-success-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cancellation-success-modal.component.scss',
})
export class CancellationSuccessModalComponent {
  @Input() isVisible = false;
  @Output() okay = new EventEmitter<void>();
  @Output() createAnother = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  onOkay(): void {
    this.okay.emit();
  }

  onCreateAnother(): void {
    this.createAnother.emit();
  }

  onClose(): void {
    this.close.emit();
  }
}
